import { Base, ReverLoading, toast } from '@itsrever/design-system'
import { GetInvoiceFileURLFileEnum, InvoicingapipbInvoice } from '@itsrever/rever-api'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useInvoiceFile,
  useInvoicedPortals,
  useInvoices,
  useInvoicingPortalURL
} from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import NotFound from '@/ui/pages/404.page'
import { PageHeader, StoreOnly } from '@/ui/shared'
import { Roles } from '@/utils'
import { startDownload } from './download'
import { InvoicesTable } from './invoices-table'

const { Button } = Base

export default function Billing() {
  const { t } = useTranslation()
  const profile = useAppSelector((s) => s.userApi.getMe)
  const selectedEcommerce = useAppSelector(
    (s) => s.generalData.selectedEcommerce
  )
  // getMe is dispatched from an effect, so on a direct load the role is not known yet.
  const loadingProfile =
    profile.loading === 'idle' || profile.loading === 'pending'
  const isAdmin = profile.response.user?.role === Roles.ReadWrite

  const { invoicingPortalURL } = useInvoicingPortalURL()
  const {
    invoicedPortals,
    loadingInvoicedPortals,
    portalsError,
    reloadInvoicedPortals
  } = useInvoicedPortals()
  const { invoices, loadingInvoices, invoicesError, reloadInvoices } =
    useInvoices()
  const { requestFile } = useInvoiceFile()
  const [pendingFile, setPendingFile] = useState<{
    invoiceId: string
    file: GetInvoiceFileURLFileEnum
  }>()

  if (loadingProfile || loadingInvoicedPortals) return <ReverLoading />
  // The API refuses a non-admin caller regardless of what this page renders.
  if (!isAdmin) return <NotFound />

  const handleDownload = async (
    invoice: InvoicingapipbInvoice,
    file: GetInvoiceFileURLFileEnum
  ) => {
    const invoiceId = invoice.id ?? ''
    setPendingFile({ invoiceId, file })
    try {
      const response = await requestFile({ invoiceId, file })
      if (!response.url) throw new Error('no url')
      startDownload(response.url)
    } catch {
      toast({ variant: 'error', text: t('billing.download_error') })
    } finally {
      setPendingFile(undefined)
    }
  }

  // A group REVER invoices nowhere has no portal to offer, so asking for one is a dead end.
  // Only an answer says that: a failed lookup goes to the error state below.
  const nothingToBill =
    !selectedEcommerce && !portalsError && invoicedPortals.length === 0

  const page = (
    <>
      <PageHeader
        title={t('billing.title')}
        description={t('billing.description')}
        actions={
          invoicingPortalURL ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                window.open(invoicingPortalURL, '_blank', 'noopener')
              }
            >
              {t('billing.payment_details')}
            </Button>
          ) : null
        }
      />
      <div className="flex max-w-[1000px] flex-col gap-4 px-7 py-4">
        <InvoicesTable
          invoices={nothingToBill ? [] : invoices}
          loading={!nothingToBill && loadingInvoices}
          error={invoicesError || portalsError}
          pendingFile={pendingFile}
          onRetry={() => {
            if (portalsError) reloadInvoicedPortals()
            if (invoicesError) reloadInvoices()
          }}
          onDownload={handleDownload}
        />
      </div>
    </>
  )

  if (nothingToBill || portalsError) return page
  return <StoreOnly stores={invoicedPortals}>{page}</StoreOnly>
}
